// Layer 3 seed: Plant → Area → Equipment → Loop → Tag registry derived from
// SUBPROCESSES, UNIT_OPERATIONS, CONTROL_LOOPS, and TAG_MAPPING — nothing typed twice.

import type { PlantNode } from '@/versions/v1/schema/plant-node';
import { CONTROL_LOOPS, SUBPROCESSES, UNIT_OPERATIONS } from '@/versions/v1/shared/bunker-hill-seed';
import { TAG_MAPPING } from '@/versions/v1/shared/bunker-hill-spc';

const PLANT_ID = 'bh-plant';

/** UnitOperation.area → subprocess id (mirrors Scene.transform NODE_AREAS). */
const AREA_TO_SUBPROCESS: Record<string, string> = {
  Crushing: 'sp-crush',
  Grinding: 'sp-grind',
  Classification: 'sp-grind',
  'Pb circuit': 'sp-pb',
  'Zn circuit': 'sp-zn',
  Dewatering: 'sp-filter',
  Tailings: 'sp-paste',
  'Process water': 'sp-water',
};

const LOOP_PARENT: Record<string, { subprocessId: string; equipmentId?: string }> = {
  'loop-lic4010': { subprocessId: 'sp-pb', equipmentId: 'PB01' },
  'loop-lic4013': { subprocessId: 'sp-pb', equipmentId: 'PB01' },
  'loop-wic1201': { subprocessId: 'sp-grind', equipmentId: 'BM01' },
  'loop-aic5002': { subprocessId: 'sp-zn', equipmentId: 'ZN01' },
};

function buildPlantNodes(): PlantNode[] {
  const nodes: PlantNode[] = [
    { id: PLANT_ID, level: 'plant', label: 'Lead-zinc concentrator' },
  ];

  for (const sp of SUBPROCESSES) {
    nodes.push({
      id: sp.id,
      level: 'area',
      parentId: PLANT_ID,
      label: sp.name,
      subprocessId: sp.id,
    });
  }

  for (const unit of UNIT_OPERATIONS) {
    const subprocessId = AREA_TO_SUBPROCESS[unit.area];
    if (!subprocessId) continue;
    nodes.push({
      id: unit.id,
      level: 'equipment',
      parentId: subprocessId,
      label: unit.name,
      subprocessId,
    });
  }

  // Pb flotation stages — subprocess description "rougher → regrind → cleaner"
  // (§8.4 sample descriptions · subprocess stage list).
  for (const stage of [
    { id: 'PB01-RGH', label: 'Pb rougher' },
    { id: 'PB01-RGR', label: 'Pb regrind' },
    { id: 'PB01-CLN', label: 'Pb cleaner' },
  ] as const) {
    nodes.push({
      id: stage.id,
      level: 'equipment',
      parentId: 'sp-pb',
      label: stage.label,
      subprocessId: 'sp-pb',
    });
  }

  for (const loop of CONTROL_LOOPS) {
    const parent = LOOP_PARENT[loop.id];
    if (!parent) continue;
    nodes.push({
      id: loop.id,
      level: 'loop',
      parentId: parent.equipmentId ?? parent.subprocessId,
      label: loop.tag,
      subprocessId: parent.subprocessId,
    });
  }

  for (const row of TAG_MAPPING) {
    const parentId = row.gateLoopId ?? row.subprocessId;
    nodes.push({
      id: `tag-${row.role}`,
      level: 'tag',
      parentId,
      label: row.instrumentTag,
      subprocessId: row.subprocessId,
    });
  }

  return nodes;
}

export const PLANT_NODES: readonly PlantNode[] = buildPlantNodes();

const nodeById = new Map(PLANT_NODES.map((n) => [n.id, n] as const));

export function plantNode(id: string): PlantNode | undefined {
  return nodeById.get(id);
}

export function plantNodeLabel(id: string): string {
  if (id === 'external') return 'external';
  return nodeById.get(id)?.label ?? id;
}

/** FMEA attachment card labels — keyed to SUBPROCESSES ids. */
export const SUBPROCESS_FMEA_LABELS: Readonly<Record<string, string>> = {
  'sp-crush': 'Crushing',
  'sp-grind': 'Grinding',
  'sp-pb': 'Pb flotation',
  'sp-zn': 'Zn flotation',
  'sp-filter': 'Dewatering',
  'sp-paste': 'Paste backfill',
  'sp-water': 'Process water',
};

export function subprocessFmeaLabel(nodeId: string): string {
  return SUBPROCESS_FMEA_LABELS[nodeId] ?? plantNodeLabel(nodeId);
}

/** Resolve a stream endpoint to its owning subprocess (area) id. */
export function subprocessForNodeId(nodeId: string): string | undefined {
  if (nodeId.startsWith('sp-')) return nodeId;
  const node = nodeById.get(nodeId);
  return node?.subprocessId;
}

/** True when both stream ends sit inside the same subprocess area. */
export function isInteriorStream(from: string, to: string): boolean {
  const area = subprocessForNodeId(from);
  const other = subprocessForNodeId(to);
  return area != null && area === other;
}
